/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import api from '../lib/api';
import { tableLabel } from '../lib/tableLabel';

/**
 * StaleTabsAlert
 * -------------------------------------------------------------
 * Owner request (May 31 2026 — "Do c" path of "what about tab expiry?"
 * conversation): surface tabs that have had no activity in 4+ hours so
 * the manager / bartender can settle or chase before Day Close
 * auto-forfeits the $30 deposit.
 *
 * Rendered as a collapsible banner at the top of the Bar Panel
 * Payments tab AND the Live Orders page. Polls /api/tabs/stale every
 * 60s. Renders nothing when there are no stale tabs (visually quiet
 * unless action is needed).
 */
export default function StaleTabsAlert() {
  const [stale, setStale] = useState([]);
  const [expanded, setExpanded] = useState(false);

  const fetchStale = useCallback(async () => {
    try {
      const { data } = await api.get('/tabs/stale');
      setStale(Array.isArray(data) ? data : []);
    } catch { /* poll will retry */ }
  }, []);

  useEffect(() => {
    fetchStale();
    const i = setInterval(fetchStale, 60_000);
    return () => clearInterval(i);
  }, [fetchStale]);

  if (stale.length === 0) return null;

  return (
    <div className="mb-4 border border-amber-500/40 bg-amber-500/[0.08] rounded-sm" data-testid="stale-tabs-alert">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left"
        data-testid="stale-tabs-alert-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle className="w-4 h-4 text-amber-300 shrink-0" />
          <span className="text-xs sm:text-sm font-sans uppercase tracking-[0.15em] font-semibold text-amber-200">
            {stale.length} stale tab{stale.length > 1 ? 's' : ''} · settle or chase before Day Close auto-forfeit
          </span>
        </div>
        <span className="text-[10px] uppercase tracking-[0.15em] font-sans text-amber-300/70">{expanded ? 'Hide' : 'Show'}</span>
      </button>
      {expanded && (
        <ul className="border-t border-amber-500/20 divide-y divide-amber-500/10" data-testid="stale-tabs-alert-list">
          {stale.map(t => (
            <li key={t.id} className="px-3 py-2 flex items-center gap-3 flex-wrap" data-testid={`stale-tab-row-${t.id}`}>
              <span className="text-gold font-serif text-lg font-semibold leading-none">{tableLabel(t.table_number || '—')}</span>
              <span className="text-white text-sm font-sans truncate flex-1 min-w-0">{t.customer_name || 'guest'}</span>
              <span className="text-amber-200 font-mono text-xs flex items-center gap-1">
                <Clock className="w-3 h-3" /> {t.hours_inactive}h idle
              </span>
              <span className="text-amber-100 font-mono text-xs">
                ${Number(t.current_total || 0).toFixed(2)} · ${Number(t.unpaid_balance || 0).toFixed(2)} unpaid
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
